import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, Divider } from '@mui/material';
import { CodeBracketIcon } from '@heroicons/react/24/outline';
import styles from '../../styles/query/QuerySyntaxHelpModal.module.scss';


const QuerySyntaxHelpModal = ({ open, onClose }) => {
  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      className={styles.dialog}
      PaperProps={{
        className: styles.dialogPaper
      }}
    >
      <DialogTitle className={styles.dialogTitle}>
        <Box display="flex" alignItems="center" gap={1}>
          <CodeBracketIcon className={styles.titleIcon} />
          <Typography variant="h6" component="span">
            Query Syntax Help
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent className={styles.dialogContent}>
        <div className={styles.helpContent}>
          {/* Introduction */}
          <section className={styles.section}>
            <Typography variant="subtitle1" className={styles.sectionTitle}>
              Overview
            </Typography>
            <Typography variant="body2" className={styles.sectionText}>
              Build powerful queries using fields, operators, and logical expressions. 
              Use parentheses to group conditions and control operator precedence.
            </Typography>
          </section>

          <Divider className={styles.divider} />

          {/* Quick Filter Searches */}
          <section className={styles.section}>
            <Typography variant="subtitle1" className={styles.sectionTitle}>
              Quick Filter Searches
            </Typography>
            <Typography variant="body2" className={styles.sectionText}>
              In <strong>UI mode</strong>, use the quick filter search box to quickly find test cases without writing query syntax. 
              Simply type your search term, and it will automatically search across multiple fields simultaneously.
            </Typography>
            <div className={styles.exampleBlock} style={{ marginTop: '12px' }}>
              <Typography variant="body2" className={styles.exampleLabel}>How it works:</Typography>
              <Typography variant="body2" className={styles.sectionText} style={{ marginTop: '8px' }}>
                The quick filter searches for your term in the following fields:
              </Typography>
              <ul className={styles.fieldList} style={{ marginTop: '8px' }}>
                <li><code>title</code> - Test case title</li>
                <li><code>key</code> - Test case key/identifier</li>
                <li><code>description</code> - Test case description</li>
              </ul>
              <Typography variant="body2" className={styles.sectionText} style={{ marginTop: '12px' }}>
                The search is case-insensitive and matches if your term appears <strong>anywhere</strong> in any of these fields. 
                It's equivalent to writing:
              </Typography>
              <div className={styles.exampleBlock} style={{ marginTop: '8px' }}>
                <code className={styles.codeExample}>
                  title contains "search term" OR key contains "search term" OR description contains "search term"
                </code>
              </div>
            </div>
            <div className={styles.exampleBlock} style={{ marginTop: '12px' }}>
              <Typography variant="body2" className={styles.exampleLabel}>Example:</Typography>
              <Typography variant="body2" className={styles.sectionText} style={{ marginTop: '4px' }}>
                Type <code>"login"</code> in the quick filter to find all test cases where "login" appears in the title, key, or description.
              </Typography>
            </div>
          </section>

          <Divider className={styles.divider} />

          {/* Basic Syntax */}
          <section className={styles.section}>
            <Typography variant="subtitle1" className={styles.sectionTitle}>
              Basic Syntax
            </Typography>
            <div className={styles.exampleBlock}>
              <code className={styles.codeExample}>
                field operator value
              </code>
            </div>
            <div className={styles.exampleBlock}>
              <Typography variant="body2" className={styles.exampleLabel}>Example:</Typography>
              <code className={styles.codeExample}>
                status = "New"
              </code>
            </div>
          </section>

          <Divider className={styles.divider} />

          {/* Fields */}
          <section className={styles.section}>
            <Typography variant="subtitle1" className={styles.sectionTitle}>
              Available Fields
            </Typography>
            <Typography variant="body2" className={styles.sectionText}>
              Use any field name from your test cases. Common fields include:
            </Typography>
            <ul className={styles.fieldList}>
              <li><code>status</code> - Test case status</li>
              <li><code>type</code> - Test case type</li>
              <li><code>priority</code> - Priority level</li>
              <li><code>title</code> - Test case title</li>
              <li><code>created_at</code> - Creation date</li>
              <li><code>updated_at</code> - Last update date</li>
              <li><code>generated_by_user</code> - Creator username</li>
            </ul>
          </section>

          <Divider className={styles.divider} />

          {/* Comparison Operators */}
          <section className={styles.section}>
            <Typography variant="subtitle1" className={styles.sectionTitle}>
              Comparison Operators
            </Typography>
            <div className={styles.operatorTable}>
              <div className={styles.operatorRow}>
                <code className={styles.operatorCode}>=</code>
                <Typography variant="body2" className={styles.operatorDesc}>Equals</Typography>
                <code className={styles.operatorExample}>status = "New"</code>
              </div>
              <div className={styles.operatorRow}>
                <code className={styles.operatorCode}>!=</code>
                <Typography variant="body2" className={styles.operatorDesc}>Not equals</Typography>
                <code className={styles.operatorExample}>status != "Retired"</code>
              </div>
              <div className={styles.operatorRow}>
                <code className={styles.operatorCode}>&gt;</code>
                <Typography variant="body2" className={styles.operatorDesc}>Greater than</Typography>
                <code className={styles.operatorExample}>created_at &gt; "2024-01-01"</code>
              </div>
              <div className={styles.operatorRow}>
                <code className={styles.operatorCode}>&lt;</code>
                <Typography variant="body2" className={styles.operatorDesc}>Less than</Typography>
                <code className={styles.operatorExample}>created_at &lt; "2024-12-31"</code>
              </div>
              <div className={styles.operatorRow}>
                <code className={styles.operatorCode}>&gt;=</code>
                <Typography variant="body2" className={styles.operatorDesc}>Greater than or equal</Typography>
                <code className={styles.operatorExample}>created_at &gt;= "2024-01-01"</code>
              </div>
              <div className={styles.operatorRow}>
                <code className={styles.operatorCode}>&lt;=</code>
                <Typography variant="body2" className={styles.operatorDesc}>Less than or equal</Typography>
                <code className={styles.operatorExample}>created_at &lt;= "2024-12-31"</code>
              </div>
            </div>
          </section>

          <Divider className={styles.divider} />

          {/* String Operators */}
          <section className={styles.section}>
            <Typography variant="subtitle1" className={styles.sectionTitle}>
              String Operators
            </Typography>
            <div className={styles.operatorTable}>
              <div className={styles.operatorRow}>
                <code className={styles.operatorCode}>contains</code>
                <Typography variant="body2" className={styles.operatorDesc}>Contains substring</Typography>
                <code className={styles.operatorExample}>title contains "login"</code>
              </div>
              <div className={styles.operatorRow}>
                <code className={styles.operatorCode}>startsWith</code>
                <Typography variant="body2" className={styles.operatorDesc}>Starts with prefix</Typography>
                <code className={styles.operatorExample}>title startsWith "User"</code>
              </div>
              <div className={styles.operatorRow}>
                <code className={styles.operatorCode}>endsWith</code>
                <Typography variant="body2" className={styles.operatorDesc}>Ends with suffix</Typography>
                <code className={styles.operatorExample}>title endsWith "Test"</code>
              </div>
            </div>
          </section>

          <Divider className={styles.divider} />

          {/* Array Operators */}
          <section className={styles.section}>
            <Typography variant="subtitle1" className={styles.sectionTitle}>
              Array Operators
            </Typography>
            <div className={styles.operatorTable}>
              <div className={styles.operatorRow}>
                <code className={styles.operatorCode}>in</code>
                <Typography variant="body2" className={styles.operatorDesc}>Value in list</Typography>
                <code className={styles.operatorExample}>status in ["New", "In Progress"]</code>
              </div>
              <div className={styles.operatorRow}>
                <code className={styles.operatorCode}>notIn</code>
                <Typography variant="body2" className={styles.operatorDesc}>Value not in list</Typography>
                <code className={styles.operatorExample}>status notIn ["Retired", "Archived"]</code>
              </div>
            </div>
            <Typography variant="body2" className={styles.sectionText} style={{ marginTop: '12px' }}>
              Use square brackets <code>[ ]</code> to create lists. Separate multiple values with commas.
            </Typography>
          </section>

          <Divider className={styles.divider} />

          {/* Null Operators */}
          <section className={styles.section}>
            <Typography variant="subtitle1" className={styles.sectionTitle}>
              Null/Empty Operators
            </Typography>
            <div className={styles.operatorTable}>
              <div className={styles.operatorRow}>
                <code className={styles.operatorCode}>isEmpty</code>
                <Typography variant="body2" className={styles.operatorDesc}>Field is empty or null</Typography>
                <code className={styles.operatorExample}>description isEmpty</code>
              </div>
              <div className={styles.operatorRow}>
                <code className={styles.operatorCode}>isNotEmpty</code>
                <Typography variant="body2" className={styles.operatorDesc}>Field has a value</Typography>
                <code className={styles.operatorExample}>description isNotEmpty</code>
              </div>
            </div>
          </section>

          <Divider className={styles.divider} />

          {/* Logical Operators */}
          <section className={styles.section}>
            <Typography variant="subtitle1" className={styles.sectionTitle}>
              Logical Operators
            </Typography>
            <div className={styles.operatorTable}>
              <div className={styles.operatorRow}>
                <code className={styles.operatorCode}>AND</code>
                <Typography variant="body2" className={styles.operatorDesc}>Both conditions must be true</Typography>
                <code className={styles.operatorExample}>status = "New" AND priority = "High"</code>
              </div>
              <div className={styles.operatorRow}>
                <code className={styles.operatorCode}>OR</code>
                <Typography variant="body2" className={styles.operatorDesc}>At least one condition must be true</Typography>
                <code className={styles.operatorExample}>status = "New" OR status = "In Progress"</code>
              </div>
              <div className={styles.operatorRow}>
                <code className={styles.operatorCode}>NOT</code>
                <Typography variant="body2" className={styles.operatorDesc}>Negates a condition</Typography>
                <code className={styles.operatorExample}>NOT status = "Retired"</code>
              </div>
            </div>
            <Typography variant="body2" className={styles.sectionText} style={{ marginTop: '12px' }}>
              <strong>Operator Precedence:</strong> NOT &gt; AND &gt; OR. Use parentheses to override precedence.
            </Typography>
          </section>

          <Divider className={styles.divider} />

          {/* Complex Examples */}
          <section className={styles.section}>
            <Typography variant="subtitle1" className={styles.sectionTitle}>
              Complex Examples
            </Typography>
            <div className={styles.exampleBlock}>
              <Typography variant="body2" className={styles.exampleLabel}>Multiple conditions:</Typography>
              <code className={styles.codeExample}>
                status = "New" AND (priority = "High" OR priority = "Critical")
              </code>
            </div>
            <div className={styles.exampleBlock}>
              <Typography variant="body2" className={styles.exampleLabel}>Date range:</Typography>
              <code className={styles.codeExample}>
                created_at &gt;= "2024-01-01" AND created_at &lt;= "2024-12-31"
              </code>
            </div>
            <div className={styles.exampleBlock}>
              <Typography variant="body2" className={styles.exampleLabel}>Text search with exclusion:</Typography>
              <code className={styles.codeExample}>
                title contains "login" AND NOT status = "Retired"
              </code>
            </div>
            <div className={styles.exampleBlock}>
              <Typography variant="body2" className={styles.exampleLabel}>Multiple status values:</Typography>
              <code className={styles.codeExample}>
                status in ["New", "Rejected", "In Review"]
              </code>
            </div>
          </section>

          <Divider className={styles.divider} />

          {/* Tips */}
          <section className={styles.section}>
            <Typography variant="subtitle1" className={styles.sectionTitle}>
              Tips
            </Typography>
            <ul className={styles.tipsList}>
              <li>Use double quotes <code>"</code> or single quotes <code>'</code> for string values</li>
              <li>Use square brackets <code>[ ]</code> for lists with multiple values</li>
              <li>Use parentheses <code>( )</code> to group conditions and control precedence</li>
              <li>Press <strong>Enter</strong> to execute the query. </li>
              <li>Use autocomplete suggestions while typing for faster query building</li>
              <li>Date format: <code>YYYY-MM-DD</code> (e.g., <code>"2024-01-15"</code>)</li>
            </ul>
          </section>
        </div>
      </DialogContent>

      <DialogActions className={styles.dialogActions}>
        <Button 
          onClick={onClose} 
          variant="contained"
          className={styles.closeButton}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuerySyntaxHelpModal;

